'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { marked, type MarkedOptions } from 'marked';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Upload, X, ArrowRight, Download, Eye, Code } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import DOMPurify from 'dompurify';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { markdownToHtmlContent } from '../../data/markdown-to-html-content';

const sampleMarkdown = `# Welcome to Markdown to HTML Converter

This is a sample Markdown document that you can convert to HTML.

## Features

- Convert **Markdown** to clean _HTML_
- Supports common Markdown syntax
- Preserves formatting and structure
- Code syntax highlighting

## Example Code

\`\`\`javascript
function helloWorld() {
  console.log('Hello, world!');
  return <div>Hello, world!</div>;
}
\`\`\`

## Links and Images

[Visit our website](https://example.com)

![Example Image](https://via.placeholder.com/150)

> This is a blockquote. It can span multiple lines.

## Tables

| Syntax      | Description |
| ----------- | ----------- |
| Header      | Title       |
| Paragraph   | Text        |
`;

const sampleHtml = `<!DOCTYPE html>
<html>
<head>
    <title>Sample HTML</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
        pre { background: #f4f4f4; padding: 10px; border-radius: 5px; overflow-x: auto; }
        code { font-family: 'Courier New', monospace; }
        table { border-collapse: collapse; width: 100%; margin: 20px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f4f4f4; }
        blockquote { border-left: 4px solid #ddd; padding-left: 15px; color: #666; }
        img { max-width: 100%; height: auto; }
    </style>
</head>
<body>
    <ToolPageIntro title="Markdown to HTML Converter - Free Online Document Converter">
                Convert Markdown to clean, well-formatted HTML instantly. Perfect for documentation, blogs, and web content creation.
            </ToolPageIntro>
    
    <h2>Features</h2>
    <ul>
        <li>Convert <strong>Markdown</strong> to clean <em>HTML</em></li>
        <li>Supports common Markdown syntax</li>
        <li>Preserves formatting and structure</li>
    </ul>
    
    <h2>Example Code</h2>
    <pre><code>console.log('Hello, world!');</code></pre>
    
    <p>Visit our <a href="https://example.com">website</a> for more information.</p>
</body>
</html>`;

// Configure marked with valid options for the current version
marked.use({
    gfm: true,
    breaks: true,
    async: true, // Enable async mode
    // Only include options that are valid in the current version of marked
});

// Create a custom renderer with proper type assertions
const renderer = new marked.Renderer();

// Add a custom code renderer with proper type handling
renderer.code = function ({ text, lang, escaped }: { text: string, lang?: string, escaped?: boolean }) {
    const language = lang || '';
    const escapedCode = escaped ? text : text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    return `<pre class="language-${language}"><code class="language-${language}">${escapedCode}</code></pre>`;
};

// Configure marked with our renderer
marked.setOptions({
    renderer,
});

export default function MarkdownToHtmlTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [isConverting, setIsConverting] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const { toast } = useToast();
    const outputRef = useRef<HTMLDivElement>(null);

    const convertMarkdown = useCallback(async (markdownString: string) => {
        try {
            if (!markdownString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            setIsConverting(true);

            // Convert markdown to HTML - using marked.parse which returns a Promise
            const html = await marked.parse(markdownString);

            // Sanitize HTML to prevent XSS
            const cleanHtml = DOMPurify.sanitize(html, {
                ADD_ATTR: ['target'], // Allow target="_blank" for links
                ALLOW_UNKNOWN_PROTOCOLS: true
            });

            // Create a complete HTML document
            const fullHtml = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Converted Markdown</title>
    <style>
        body { 
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; 
            line-height: 1.6; 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px; 
            color: #333;
        }
        pre { 
            background: #f8f8f8; 
            padding: 16px; 
            border-radius: 4px; 
            overflow-x: auto; 
            font-size: 14px;
            margin: 1em 0;
        }
        code { 
            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
            background: #f8f8f8;
            padding: 0.2em 0.4em;
            border-radius: 3px;
            font-size: 85%;
        }
        pre code { 
            padding: 0;
            background: transparent;
            border-radius: 0;
        }
        table { 
            border-collapse: collapse; 
            width: 100%; 
            margin: 20px 0;
            font-size: 14px;
        }
        th, td { 
            border: 1px solid #e1e4e8; 
            padding: 8px 12px; 
            text-align: left; 
        }
        th { 
            background-color: #f6f8fa; 
            font-weight: 600;
        }
        tr:nth-child(even) {
            background-color: #f6f8fa;
        }
        blockquote { 
            border-left: 4px solid #dfe2e5; 
            padding-left: 16px; 
            color: #6a737d;
            margin: 16px 0;
            padding: 0 1em;
        }
        img { 
            max-width: 100%; 
            height: auto; 
            border-radius: 4px;
        }
        a {
            color: #0366d6;
            text-decoration: none;
        }
        a:hover {
            text-decoration: underline;
        }
        h1, h2, h3, h4, h5, h6 {
            margin-top: 24px;
            margin-bottom: 16px;
            font-weight: 600;
            line-height: 1.25;
        }
        h1 { font-size: 2em; border-bottom: 1px solid #eaecef; padding-bottom: 0.3em; }
        h2 { font-size: 1.5em; border-bottom: 1px solid #eaecef; padding-bottom: 0.3em; }
        h3 { font-size: 1.25em; }
        h4 { font-size: 1em; }
        h5 { font-size: 0.875em; }
        h6 { font-size: 0.85em; color: #6a737d; }
        ul, ol { padding-left: 2em; }
        li { margin-bottom: 0.25em; }
        p { margin-top: 0; margin-bottom: 16px; }
        hr { 
            height: 0.25em;
            padding: 0;
            margin: 24px 0;
            background-color: #e1e4e8;
            border: 0;
        }
    </style>
</head>
<body>
    ${cleanHtml}
</body>
</html>`;

            setOutput(fullHtml);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid Markdown'}`);
            setOutput('');
        } finally {
            setIsConverting(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            convertMarkdown(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input, convertMarkdown]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);

        // Reset the file input
        e.target.value = '';
    };

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The HTML has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'text/html;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName ? fileName.replace(/\.\w+$/, '.html') : 'converted.html';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
        setFileName(null);
    };

    const loadSample = () => {
        setInput(sampleMarkdown);
        setFileName('sample.md');
    };

    // Get the HTML content without the surrounding document for preview
    const getPreviewContent = () => {
        if (!output) return '';
        const bodyMatch = output.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        return bodyMatch ? bodyMatch[1] : output;
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                    Markdown to HTML Converter
                </h1>
                <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
                    Convert Markdown to clean, well-formatted HTML
                </p>
            </div>

            <div className="w-full max-w-7xl mx-auto mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="relative flex-1">
                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                            Upload Markdown File
                        </label>
                        <label className="flex items-center justify-center w-full px-4 py-2 bg-background border border-dashed border-input rounded-md cursor-pointer hover:bg-accent/50 transition-colors">
                            <Upload className="h-4 w-4 mr-2" />
                            <span className="text-sm truncate max-w-[200px]">
                                {fileName || 'Choose a file...'}
                            </span>
                            <input
                                type="file"
                                accept=".md,.markdown,text/markdown,text/x-markdown"
                                className="hidden"
                                onChange={handleFileUpload}
                                disabled={isConverting}
                            />
                        </label>
                        {fileName && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFileName(null);
                                    setInput('');
                                }}
                                className="mt-1 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                disabled={isConverting}
                            >
                                <X className="h-3 w-3" />
                                Remove file
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadSample}
                            className="gap-1.5"
                            disabled={isConverting}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Sample
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            disabled={!input || isConverting}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        Markdown Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName || 'Paste your Markdown here'}
                                    </CardDescription>
                                </div>
                                <div className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                                    MARKDOWN
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        '# Markdown to HTML Converter\n\n' +
                                        '## Features\n\n' +
                                        '- Convert **Markdown** to clean _HTML_\n' +
                                        '- Supports common Markdown syntax\n' +
                                        '- Preserves formatting and structure\n\n' +
                                        '```javascript\n' +
                                        '// Example code\n' +
                                        'function hello() {\n' +
                                        '  console.log(\'Hello, world!\');\n' +
                                        '}\n' +
                                        '```'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                    disabled={isConverting}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-4 lg:my-0">
                    <div className="flex flex-col items-center">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <ArrowRight className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                            Convert to
                        </p>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        HTML Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName ?
                                            fileName.replace(/\.\w+$/, '.html') :
                                            'Your HTML will appear here'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant={showPreview ? "outline" : "default"}
                                        size="sm"
                                        onClick={() => setShowPreview(!showPreview)}
                                        className="gap-1.5"
                                        disabled={!output || isConverting}
                                    >
                                        {showPreview ? (
                                            <>
                                                <Code className="h-3.5 w-3.5" />
                                                Show Code
                                            </>
                                        ) : (
                                            <>
                                                <Eye className="h-3.5 w-3.5" />
                                                Preview
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!output || isConverting}
                                        className="gap-1.5"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output || isConverting}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                {showPreview ? (
                                    <div
                                        ref={outputRef}
                                        className="flex-1 p-4 overflow-auto"
                                        dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                                    />
                                ) : (
                                    <Textarea
                                        value={output}
                                        readOnly
                                        className={cn(
                                            'flex-1 font-mono text-sm border-0 bg-muted/20',
                                            'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                            error && 'text-destructive/70',
                                            'whitespace-pre' // Preserve formatting
                                        )}
                                        placeholder="Your HTML will appear here..."
                                    />
                                )}
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your HTML will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                                {isConverting && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={markdownToHtmlContent.title}
                    description={markdownToHtmlContent.description}
                    content={markdownToHtmlContent.content}
                    faqs={markdownToHtmlContent.faqs}
                    relatedTools={markdownToHtmlContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
